import pdfMake from 'pdfmake';
import { 
  createFooter, 
  getDefaultStyles
} from './reportPdfUtils';

export const createFinanceReport = async (data) => {
  try {
    const logoBase64 = data.logoBase64;
    
    return {
      footer: createFooter(),
      content: [
        ...createHeader(data, logoBase64),
        createTransactionsTable(data),
        ...(await createReceiptsSection(data))
      ],
      styles: getDefaultStyles(),
      defaultStyle: {
        fontSize: 7
      },
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [15, 25, 15, 30],
    };
  } catch (error) {
    console.error('Error creating finance report:', error);
    throw error;
  }
};

const createHeader = (data, logoBase64) => {
  const headerContent = [];
  
  headerContent.push({
    columns: [
      logoBase64 ? {
        width: '20%',
        image: logoBase64,
        fit: [50, 40],
        margin: [0, 5, 0, 0]
      } : { width: '20%', text: '' },
      {
        width: '60%',
        text: `${data.clientName} - ${data.reportDate}`,
        style: 'headerClientName',
        alignment: 'center',
        margin: [0, 10, 0, 0]
      },
      {
        width: '20%',
        text: ''
      }
    ],
    margin: [0, 0, 0, 20]
  });

  headerContent.push({
    columns: [
      {
        width: '33%',
        text: `Account Type: ${data.transactionType}`,
        style: 'headerAccountInfo'
      },
      {
        width: '33%',
        text: `Opening Balance: $${parseFloat(data.balances.startingBalance || 0).toFixed(2)}`,
        style: 'headerAccountInfo',
        alignment: 'center'
      },
      {
        width: '34%',
        text: `Closing Balance: $${parseFloat(data.balances.endingBalance || 0).toFixed(2)}`,
        style: 'headerAccountInfo',
        alignment: 'right'
      }
    ],
    margin: [0, 0, 0, 20]
  });
  
  return headerContent;
};

const createTransactionsTable = (data) => {
  const tableBody = generateTransactionRows(data.transactions);
  
  return {
    table: {
      headerRows: 1,
      widths: ['8%', '7%', '9%', '7%', '7%', '10%', '24%', '16%', '12%'],
      body: tableBody,
      keepWithHeaderRows: 1,
      dontBreakRows: true
    },
    layout: {
      fillColor: function(rowIndex) {
        if (rowIndex === 0) return '#1976d2';
        return (rowIndex % 2 === 0) ? '#fafafa' : '#ffffff';
      },
      hLineWidth: function(i, node) {
        if (i === 0) return 0;
        if (i === 1) return 2;
        if (i === node.table.body.length) return 1;
        return 0.5;
      },
      vLineWidth: function(i, node) {
        return i === 0 || i === node.table.widths.length ? 1 : 0.5;
      },
      hLineColor: function(i) {
        if (i === 1) return '#1976d2';
        return '#e0e0e0';
      },
      vLineColor: function() {
        return '#e0e0e0';
      },
      paddingLeft: function() { return 8; },
      paddingRight: function() { return 8; },
      paddingTop: function() { return 6; },
      paddingBottom: function() { return 6; }
    },
    margin: [0, 0, 0, 20],
    pageBreak: 'auto',
    unbreakable: false
  };
};

const generateTransactionRows = (transactions) => {
  const tableBody = [
    [
      { text: 'DATE', style: 'modernTableHeader', color: 'white' },
      { text: 'TYPE', style: 'modernTableHeader', color: 'white' },
      { text: 'METHOD', style: 'modernTableHeader', color: 'white' },
      { text: 'AMOUNT', style: 'modernTableHeader', color: 'white' },
      { text: 'BALANCE', style: 'modernTableHeader', color: 'white' },
      { text: 'PAYEE', style: 'modernTableHeader', color: 'white' },
      { text: 'NOTES', style: 'modernTableHeader', color: 'white' },
      { text: 'STAFF', style: 'modernTableHeader', color: 'white' },
      { text: 'CLIENT', style: 'modernTableHeader', color: 'white' }
    ]
  ];

  if (transactions && transactions.length > 0) {
    transactions.forEach((transaction) => {
      const rowStyle = 'modernTableCell';
      
      tableBody.push([
        { text: transaction.date || '', style: rowStyle },
        { 
          text: transaction.type || '', 
          style: rowStyle
        },
        { text: transaction.methods || '', style: rowStyle },
        { 
          text: transaction.amount || '', 
          style: 'modernTableAmount'
        },
        { text: transaction.balance || '', style: 'tableBalance' },
        { text: transaction.payee || '', style: rowStyle },
        { text: transaction.notes || '', style: rowStyle },
        { text: transaction.staffSignature || '', style: rowStyle },
        transaction.clientSignature ? 
          { image: transaction.clientSignature, width: 45, height: 25, margin: [2, 2, 2, 2] } : 
          { text: 'N/A', style: 'modernTableNA' }
      ]);
    });
  } else {
    tableBody.push([
      { 
        text: 'No transactions found for the selected period', 
        colSpan: 9, 
        alignment: 'center', 
        style: 'tableOdd' 
      }, '', '', '', '', '', '', '', ''
    ]);
  }

  return tableBody;
};


const createReceiptsSection = async (data) => {
  const receiptsContent = [];
  
  if (data.receipts && data.receipts.length > 0) {
    receiptsContent.push(
      { text: '', pageBreak: 'before', margin: [0, 0, 0, 0] },
      { text: 'Receipt Documentation', style: 'sectionHeader', alignment: 'center', margin: [0, 15, 0, 25] }
    );

    const receiptsPerRow = 2;
    const rows = [];
    
    for (let i = 0; i < data.receipts.length; i += receiptsPerRow) {
      const rowReceipts = data.receipts.slice(i, i + receiptsPerRow);
      const rowContent = [];

      for (const receipt of rowReceipts) {
        rowContent.push({
          stack: [
            {
              text: receipt.date || 'Date not specified',
              style: 'receiptDateHeader',
              alignment: 'center',
              margin: [0, 0, 0, 8]
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    receipt.imageBase64 ? {
                      image: receipt.imageBase64,
                      width: 250,
                      height: 180,
                      alignment: 'center',
                      margin: [5, 5, 5, 5]
                    } : {
                      text: 'Receipt not available',
                      style: 'receiptNotAvailable',
                      alignment: 'center',
                      margin: [5, 60, 5, 60]
                    }
                  ]
                ]
              },
              layout: {
                fillColor: function() { return '#ffffff'; },
                hLineWidth: function() { return 1; },
                vLineWidth: function() { return 1; },
                hLineColor: function() { return '#dee2e6'; },
                vLineColor: function() { return '#dee2e6'; }
              }
            }
          ],
          width: '48%',
          margin: [0, 0, 10, 20]
        });
      }

      while (rowContent.length < receiptsPerRow) {
        rowContent.push({ text: '', width: '48%' });
      }

      rows.push({
        columns: rowContent,
        margin: [0, 0, 0, 10],
        pageBreak: 'auto',
        unbreakable: true
      });
    }

    receiptsContent.push(...rows);
  }

  return receiptsContent;
};

export const generateReport = async (data, filename = 'finance-report.pdf', action = 'download') => {
  try {
    const doc = await createFinanceReport(data);
    
    if (action.toLowerCase() === 'print') {
      pdfMake.createPdf(doc).print();
    } else if (action.toLowerCase() === 'download') {
      pdfMake.createPdf(doc).download(filename);
    } else {
      console.warn(`Unsupported action: ${action}. Defaulting to download.`);
      pdfMake.createPdf(doc).download(filename);
    }
  } catch (error) {
    console.error(`Error ${action.toLowerCase() === 'print' ? 'printing' : 'downloading'} finance report:`, error);
    throw error;
  }
};